import { useState, useEffect } from 'react';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useAuth } from '@/contexts/AuthContext';

interface DocumentType {
  id: number;
  name: string;
  description: string | null;
  isActive: number;
}

interface DocumentTypesPanelProps {
  canManage: boolean;
}

export default function DocumentTypesPanel({ canManage }: DocumentTypesPanelProps) {
  const { accessToken, user } = useAuth();
  const { data: types, loading, refetch } = useApiQuery<DocumentType[]>({
    endpoint: '/document-types',
    enabled: canManage,
  });

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const resetForm = () => {
    setName('');
    setDescription('');
    setEditingId(null);
    setError('');
  };

  const openAdd = () => {
    resetForm();
    setIsAddOpen(true);
  };

  const openEdit = (t: DocumentType) => {
    setEditingId(t.id);
    setName(t.name);
    setDescription(t.description || '');
    setError('');
    setIsAddOpen(true);
  };

  const closeModal = () => {
    setIsAddOpen(false);
    resetForm();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError('Name is required'); return; }

    setSubmitting(true);
    setError('');
    try {
      const url = editingId ? `/document-types/${editingId}` : '/document-types';
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(`${import.meta.env.VITE_API_URL || 'https://ekyc.tumkurconnect.com/api/v1'}${url}`, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          name: name.trim(),
          description: description.trim() || null,
        }),
      });
      const json = await res.json();
      if (!res.ok) {
        setError(json.message || 'Failed to save document type');
        setSubmitting(false);
        return;
      }
      closeModal();
      refetch();
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to remove this document type?')) return;
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL || 'https://ekyc.tumkurconnect.com/api/v1'}/document-types/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (res.ok) refetch();
    } catch { /* ignore */ }
  };

  if (!canManage) return null;

  return (
    <div className="bg-white rounded-xl border border-stone-100 p-6 mt-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-bold text-stone-900">Document Types</h3>
          <p className="text-stone-400 text-xs mt-0.5">Manage the list of document types available for upload.</p>
        </div>
        <button
          onClick={openAdd}
          className="bg-brand-600 hover:bg-brand-700 text-white font-semibold px-4 py-2 rounded-full text-xs transition-colors flex items-center gap-1.5 whitespace-nowrap"
        >
          <i className="ri-add-line" />
          Add Type
        </button>
      </div>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-12 bg-stone-50 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="divide-y divide-stone-50">
          {(types?.length || 0) === 0 && (
            <div className="text-center py-8 text-stone-400 text-sm">
              No document types configured yet.
            </div>
          )}
          {types?.map((t) => (
            <div key={t.id} className="flex items-center justify-between py-3 px-2 hover:bg-stone-50 rounded-lg transition-colors">
              <div className="min-w-0">
                <p className="text-stone-900 text-sm font-medium truncate">{t.name}</p>
                {t.description && <p className="text-stone-400 text-xs truncate">{t.description}</p>}
              </div>
              <div className="flex items-center gap-1 ml-3 shrink-0">
                <button
                  onClick={() => openEdit(t)}
                  className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-stone-100 text-stone-400 hover:text-stone-600 transition-colors"
                  title="Edit"
                >
                  <i className="ri-edit-line text-sm" />
                </button>
                <button
                  onClick={() => handleDelete(t.id)}
                  className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-red-50 text-stone-400 hover:text-red-500 transition-colors"
                  title="Delete"
                >
                  <i className="ri-delete-bin-line text-sm" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add / Edit Modal */}
      {isAddOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={closeModal} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h4 className="text-lg font-bold text-stone-900 mb-1">
              {editingId ? 'Edit Document Type' : 'Add Document Type'}
            </h4>
            <p className="text-stone-400 text-xs mb-4">
              {editingId ? 'Update the document type details below.' : 'Define a new document type for uploads.'}
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-stone-700 text-sm font-medium mb-1.5">Type Name *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Aadhaar Card"
                  className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                />
              </div>
              <div>
                <label className="block text-stone-700 text-sm font-medium mb-1.5">Description</label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Optional description"
                  className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-100 rounded-lg px-3 py-2 text-red-600 text-xs">
                  {error}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 py-2.5 rounded-full border border-stone-200 text-stone-600 font-medium text-sm hover:bg-stone-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-brand-600 hover:bg-brand-700 disabled:bg-brand-300 text-white font-semibold py-2.5 rounded-full text-sm transition-colors flex items-center justify-center gap-2"
                >
                  {submitting && <i className="ri-loader-4-line animate-spin" />}
                  {editingId ? 'Update' : 'Add'} Type
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}